import React, { useState, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import ReCAPTCHA from "react-google-recaptcha";
import Seo from "@/components/Seo";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const captchaRef = useRef(null);
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nextParam = new URLSearchParams(location.search).get("next");
  const dest = location.state?.from || (nextParam && nextParam.startsWith("/") ? nextParam : "/dashboard");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!captchaToken) {
      setError(t("auth.robotCheck"));
      return;
    }
    setLoading(true);
    try {
      const u = await login(email, password, captchaToken);
      toast.success(t("auth.signedIn"));
      navigate(u?.onboarding?.required ? "/welcome" : dest);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Seo title="Sign In" description="Sign in to Fire Alarm Blueprint Trainer to save your fire alarm designs, track learning progress, and access assignments." path="/login" />
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A192F] blueprint-grid relative">
        <Link to="/" className="relative z-10">
          <img src="/logo-firealarmbt-light.png" alt="FireAlarm BT — Fire Alarm Blueprint Trainer" className="h-9 w-auto" />
        </Link>
        <div className="relative z-10 max-w-md">
          <p className="font-heading text-3xl font-black leading-tight mb-4">{t("auth.signInTitle")}</p>
          <p className="text-slate-400">{t("auth.signInSubtitle")}</p>
        </div>
        <p className="text-xs text-slate-600 font-mono relative z-10">{t("footer.disclaimerTitle")}</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="font-heading text-3xl font-black mb-1">{t("auth.signInTitle")}</h1>
          <p className="text-slate-400 mb-8 text-sm">{t("auth.signInSubtitle")}</p>

          {error && <div data-testid="login-error" className="mb-4 text-sm rounded-md border border-primary/40 bg-primary/10 text-red-200 px-3 py-2">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">{t("auth.email")}</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-secondary border-white/15 focus-visible:ring-primary" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">{t("auth.password")}</Label>
              <PasswordInput id="password" data-testid="login-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                containerClassName="mt-1.5" className="bg-secondary border-white/15 focus-visible:ring-primary" placeholder="••••••••" />
              <div className="text-right -mb-2">
                <Link to="/forgot-password" data-testid="login-forgot-password" className="inline-flex items-center min-h-[44px] px-1 text-xs text-slate-400 hover:text-primary hover:underline">{t("auth.forgotPassword")}</Link>
              </div>
            </div>
          </div>

          <div className="mt-6" data-testid="login-recaptcha">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              theme="dark"
              onChange={setCaptchaToken}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          </div>

          <Button type="submit" data-testid="login-submit" disabled={loading}
            className="w-full mt-6 bg-primary hover:bg-primary/90 text-white h-11">
            {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.signIn")}
          </Button>

          <div className="flex items-center gap-3 my-6">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-500 uppercase tracking-wide">{t("auth.or")}</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <GoogleSignInButton label={t("auth.continueGoogle")} />

          <p className="text-sm text-slate-400 mt-6 text-center">
            {t("auth.noAccount")} <Link to="/register" className="text-primary hover:underline font-medium">{t("auth.createOne")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
